import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { BoxBase, DockLayout, LayoutBase, LayoutData, PanelBase, TabData } from 'rc-dock';

import { WidgetDockAdapter } from '@/app/dock/widget-dock-adapter';
import { Menu } from '@/core/menu/export';
import { ConnectionIndicator } from '@/core/messaging/export';
import { useDebounce } from '@/shared/hooks/export';
import { Context, AppContext } from '@/core/app-context/export';
import { WidgetHostContext, WidgetHost, useWidgetNavigatorRegistry } from '@/core/widgets/export';
import { WidgetNavigationProvider } from '@/core/widget-navigation/export';

import './dock.scss';
import { preferencesApi, PreferenceConstants, MyMNIPreferences } from '@/core/preferences/export';
import {
  allBulletsWidget,
  calWidget,
  fiWidget,
  fxWidget,
  hotWidget,
  searchWidget,
  widgets,
} from '@/app/dock-widgets';

const defaultLayout: LayoutData = {
  dockbox: {
    mode: 'horizontal',
    children: [
      {
        minHeight: 70,
        mode: 'vertical',
        children: [
          {
            tabs: [
              allBulletsWidget as TabData,
              fiWidget as TabData,
              fxWidget as TabData,
              searchWidget as TabData,
            ],
          },
          {
            tabs: [hotWidget as TabData],
          },
        ],
      },

      {
        minHeight: 30,
        tabs: [calWidget as TabData],
      },
    ],
  },
};

export default function DockPage() {
  const [dock, setDock] = useState<DockLayout>();
  const [layout, setLayout] = useState<LayoutBase>();
  const debouncedLayout = useDebounce(layout, 5000);
  const [currentTabId, setCurrentTabId] = useState<string>();

  const context = useContext<AppContext>(Context);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    preferencesApi.getPreferences<MyMNIPreferences>(PreferenceConstants.MY_MNI).then(val => {
      const savedDock = val?.dock;

      if (savedDock) {
        setLayout(savedDock);
      }

      setLoading(false);
    });
  }, []);

  const firstLeaf = (b: BoxBase | PanelBase) => {
    //let panel = b as PanelBase;
    let box = b as BoxBase;
    let panel = b as PanelBase;

    while (box?.children && box.children.length > 0) {
      panel = box.children[0] as PanelBase;
      box = box.children[0] as BoxBase;

      if (panel?.tabs && panel.tabs.length > 0) return panel.tabs[panel.tabs.length - 1].id;
    }

    return box?.id || b.id;
  };

  const activeTabId = () => {
    if (currentTabId && dock?.find(currentTabId)) return currentTabId;

    if (!layout) return undefined;

    return firstLeaf(layout.dockbox);
  };

  const widgetActive = (id: string) => {
    return !!dock?.find(id);
  };

  const toggleWidget = (w: WidgetDockAdapter) => {
    const existing = dock?.find(w.id);

    if (!existing) {
      const tabId = activeTabId();
      console.log('Adding tab', w.id, tabId, layout);

      if (tabId) dock?.dockMove(w as TabData, tabId, 'after-tab');
    } else {
      console.log('removing tab', w.id, existing);
      dock?.dockMove(existing as TabData, null, 'remove');
    }
  };

  const setRef = (r: DockLayout) => {
    console.log('Setting layout');

    setDock(r);
  };

  useEffect(() => {
    preferencesApi
      .upsertPreferences(PreferenceConstants.MY_MNI, { dock: debouncedLayout })
      .then(value => console.log('Saved preferences', debouncedLayout));
  }, [debouncedLayout]);

  const { registerWidgetNavigator, navigateOrWait } = useWidgetNavigatorRegistry();

  const latestRef = useRef({ widgets, widgetActive, toggleWidget });
  latestRef.current = { widgets, widgetActive, toggleWidget };

  const widgetHost: WidgetHost = useMemo(
    () => ({
      get activeWidgetIds(): string[] {
        return latestRef.current.widgets
          .filter(w => latestRef.current.widgetActive(w.id))
          .map(w => w.id);
      },
      get hostId(): string {
        return 'dock';
      },
      get currentViewId(): string {
        return 'dock';
      },
      addWidgetById(id: string) {
        const widget = latestRef.current.widgets.find(w => w.id === id);
        if (widget && !latestRef.current.widgetActive(id)) latestRef.current.toggleWidget(widget);
      },
      removeWidgetById(id: string) {
        const widget = latestRef.current.widgets.find(w => w.id === id);
        if (widget && latestRef.current.widgetActive(id)) latestRef.current.toggleWidget(widget);
      },
      async setWidgetPath(id: string, path: string) {
        const adapter = latestRef.current.widgets.find(w => w.newsWidgetId === id);
        if (adapter && !latestRef.current.widgetActive(adapter.id)) {
          latestRef.current.toggleWidget(adapter);
        }

        await navigateOrWait(id, path);
      },
      registerWidgetNavigator,
      registerActiveWidgetSelector() {
        return () => {};
      },
      async getWidgetPreferences<T>(id: string): Promise<T> {
        return (await preferencesApi.getPreferences(`widget-${id}`)) as T;
      },
      async saveWidgetPreferences<T>(id: string, t: T): Promise<T> {
        await preferencesApi.upsertPreferences(`widget-${id}`, t);
        return t;
      },
    }),

    [navigateOrWait, registerWidgetNavigator],
  );

  return (
    <div
      className={
        'd-flex flex-row h-100 w-100 position-fixed text-start top-0 start-0 end-0 bottom-0 mni-dock'
      }
    >
      <div className={'flex-shrink-1 p-2 h-100 d-flex flex-column'}>
        {widgets.map(value => (
          <div className={'mb-1'} key={'widget-control-' + value.id}>
            <button
              style={{ fontFamily: 'Inter' }}
              className={`btn btn-sm w-100 ${
                widgetActive(value.id)
                  ? 'btn-secondary active text-base-light'
                  : 'btn-outline-secondary'
              }`}
              onClick={event => toggleWidget(value)}
            >
              {value.shortTitle}
            </button>
          </div>
        ))}

        <div className={'mb-auto'}></div>

        <div className={'text-center btn-group'}>
          <span
            className={
              'text-center btn btn-outline-secondary btn-sm disabled bg-quaternary ps-1 pe-1'
            }
          >
            <small>
              <ConnectionIndicator />
            </small>
          </span>
          <Menu padding={false} />
        </div>
      </div>
      <Context.Provider value={{ showMenu: false, localPreferences: context.localPreferences }}>
        <WidgetHostContext.Provider value={widgetHost}>
          <WidgetNavigationProvider>
            <div className={'bg-quaternary flex-grow-1 flex-fill'}>
              {loading && (
                <div className="text-center mt-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                </div>
              )}
              {!loading && (
                <DockLayout
                  ref={event => setRef(event as DockLayout)}
                  layout={layout}
                  defaultLayout={defaultLayout}
                  style={{
                    width: '100%',
                    height: '100%',
                  }}
                  onLayoutChange={(newLayout, currentTabId, direction) => {
                    console.log('LENGTH', newLayout.dockbox.children.length);
                    console.log('MODE', newLayout.dockbox.mode);
                    console.log(direction);

                    setLayout(newLayout);
                    setCurrentTabId(currentTabId);
                  }}
                />
              )}
            </div>
          </WidgetNavigationProvider>
        </WidgetHostContext.Provider>
      </Context.Provider>
    </div>
  );
}
